import createAction from './createAction';
import { fetchListingPageData } from './network';
import UISettings from '../../utils/UISettings';
import {
  filterTypes,
  prepareFiltersPayload
} from '../../components/react/ListingPage/components/Filtering/filterHelpers';
import { getActiveStoredResponse } from '../../components/react/ListingPage/helpers';
import { setHeaderBanner } from './listingPageBanners';

// because icepick is implemented using commonjs modules,
// which we don't suppport, it has to be required in this way
const icepick = require('../../../node_modules/icepick/icepick.min');

// 1. Initial state
const initialState = icepick.freeze({
  list: []
});

// 2. Actions
const SET_FILTERS_SETUP = 'listingPage/filters/SET_FILTERS_SETUP';
const SET_PRICE_RANGE_FROM = 'listingPage/filters/SET_PRICE_RANGE_FROM';
const SET_PRICE_RANGE_TO = 'listingPage/filters/SET_PRICE_RANGE_TO';
const SET_DEFAULT_OPTIONS = 'listingPage/filters/SET_DEFAULT_OPTIONS';
const TOGGLE_CHECKBOX_OPTION = 'listingPage/filters/TOGGLE_CHECKBOX_OPTION';
const RESET_ALL_FILTERS = 'listingPage/filters/RESET_ALL_FILTERS';
const RESET_FILTER_OPTIONS = 'listingPage/filters/RESET_FILTER_OPTIONS';
const RECALCULATE_PRICE_RANGES = 'listingPage/filters/RECALCULATE_PRICE_RANGES';

// 3. Action creators
export function setFiltersSetup(payload) {
  return createAction(SET_FILTERS_SETUP)(payload);
}

export function setPriceRangeFrom(payload) {
  return createAction(SET_PRICE_RANGE_FROM)(payload);
}

export function setPriceRangeTo(payload) {
  return createAction(SET_PRICE_RANGE_TO)(payload);
}

export function setDefaultOptions(payload) {
  return createAction(SET_DEFAULT_OPTIONS)(payload);
}

export function resetAllFilters() {
  return (dispatch) => {
    dispatch(createAction(RESET_ALL_FILTERS)());
    const whatChanged = 'filters';
    return dispatch(fetchListingPageData(whatChanged));
  };
}

export function resetFilterOptions(data) {
  return (dispatch, getState) => {
    dispatch(createAction(RESET_FILTER_OPTIONS)(data.filterName));

    const filter = getState().listingPage.filters.list.find(
      (filterItem) => filterItem.name === data.filterName
    );

    if (filter.type === filterTypes.price && data.clearBetweenValues) {
      dispatch(createAction(RECALCULATE_PRICE_RANGES)(data));
    }

    if (data.fetchNewData) {
      const whatChanged = 'filters';
      return dispatch(fetchListingPageData(whatChanged));
    }
    return null;
  };
}

export function toggleCheckboxOption(data) {
  return (dispatch, getState) => {
    dispatch(createAction(TOGGLE_CHECKBOX_OPTION)(data));

    const filter = getState().listingPage.filters.list.find(
      (filterItem) => filterItem.name === data.filterName
    );

    if (filter.type === filterTypes.price) {
      dispatch(createAction(RECALCULATE_PRICE_RANGES)(data));
    }

    if (data.fetchNewData) {
      const whatChanged = 'filters';
      dispatch(fetchListingPageData(whatChanged));
    }
  };
}

export function recalculatePriceRanges(data) {
  return createAction(RECALCULATE_PRICE_RANGES)(data);
}

export function restorePreviousFiltersSetup() {
  return (dispatch) => {
    const currentActiveStoredResponse = getActiveStoredResponse();
    const ajaxSettings =
      UISettings.get('component.listing-page-products.ajax-settings');
    if (currentActiveStoredResponse) {
      dispatch(setFiltersSetup(prepareFiltersPayload(currentActiveStoredResponse.data.filter)));
      if (currentActiveStoredResponse.data.banner) {
        dispatch(setHeaderBanner(currentActiveStoredResponse.data.banner));
      } else {
        dispatch(setHeaderBanner(ajaxSettings.banner));
      }
    } else {
      const togglePriceRangeOptionsOff = {
        checkboxData: {
          active: false
        },
        fetchNewData: false,
        filterName: 'price',
        filterType: 'price'
      };
      dispatch(createAction(TOGGLE_CHECKBOX_OPTION)(togglePriceRangeOptionsOff));
      dispatch(setFiltersSetup(prepareFiltersPayload(ajaxSettings.filters)));
      dispatch(setHeaderBanner(ajaxSettings.banner));
    }
  };
}

// 4. Reducer
export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_FILTERS_SETUP: {
      return icepick.setIn(state, ['list'], action.payload);
    }

    case SET_PRICE_RANGE_FROM: {
      return icepick.setIn(state, ['list'], icepick.map(
        (filter) => {
          if (filter.name !== action.payload.filterName) {
            return filter;
          }
          const newFilter = icepick.setIn(
            filter, ['currentFormBetween', 'left', 'value'], action.payload.value
          );
          return icepick.setIn(newFilter,
            ['currentFormBetween', 'left', 'valid'], action.payload.valid);
        }, state.list
      ));
    }

    case SET_PRICE_RANGE_TO: {
      return icepick.setIn(state, ['list'], icepick.map(
        (filter) => {
          if (filter.name !== action.payload.filterName) {
            return filter;
          }
          const newFilter = icepick.setIn(
            filter, ['currentFormBetween', 'right', 'value'], action.payload.value
          );
          return icepick.setIn(newFilter, ['currentFormBetween', 'right', 'valid'],
            action.payload.valid);
        }, state.list
      ));
    }

    case SET_DEFAULT_OPTIONS: {
      return icepick.setIn(state, ['list'], icepick.map(
        (filter) => {
          if (filter.name !== action.payload.filterName) {
            return filter;
          }
          return icepick.setIn(filter, ['options'], action.payload.initialState);
        }, state.list
      ));
    }

    case TOGGLE_CHECKBOX_OPTION: {
      return icepick.setIn(state, ['list'], icepick.map((filter) => {
        if (filter.name === action.payload.filterName) {
          return icepick.setIn(filter, ['options'], filter.options.map((filterOption) => {
            if (filterOption.value === action.payload.checkboxData.value) {
              return { ...filterOption, active: !filterOption.active };
            }

            if (action.payload.filterType === filterTypes.price) {
              if (!action.payload.checkboxData.value) {
                return { ...filterOption, active: false };
              }
            }

            if (action.payload.filterType === filterTypes.rating) {
              // when checking rating or price checkbox, all other rating checkboxes are unchecked
              return { ...filterOption, active: false };
            }
            return filterOption;
          }));
        }
        return filter;
      }, state.list));
    }

    case RECALCULATE_PRICE_RANGES: {
      return icepick.setIn(state, ['list'], icepick.map((filter) => {
        if (filter.name === action.payload.filterName &&
            filter.type === filterTypes.price
        ) {
          const minPrices = [];
          const maxPrices = [];
          filter.options.forEach((option) => {
            if (option.active === true) {
              const optionMinPrice = option.range.left.value || filter.limitBetween.left.value;
              const optionMaxPrice = option.range.right.value || filter.limitBetween.right.value;
              minPrices.push(optionMinPrice);
              maxPrices.push(optionMaxPrice);
            }
          });

          const minPrice = Math.min(...minPrices) === Infinity ? '' : Math.min(...minPrices);
          const maxPrice = Math.max(...maxPrices) === -Infinity ? '' : Math.max(...maxPrices);

          const newFilter = icepick.setIn(
            filter, ['currentFormBetween', 'left', 'value'], minPrice
          );

          return icepick.setIn(newFilter, ['currentFormBetween', 'right', 'value'], maxPrice);
        }
        return filter;
      }, state.list));
    }

    case RESET_ALL_FILTERS: {
      return icepick.setIn(state, ['list'], icepick.map(
        (filter) => {
          let newFilter = icepick.setIn(filter, ['options'], filter.options.map(
            (filterOption) => ({ ...filterOption, active: false })
          ));
          if (newFilter.currentFormBetween) {
            newFilter = icepick.setIn(newFilter, ['currentFormBetween', 'left', 'value'], '');
            newFilter = icepick.setIn(newFilter, ['currentFormBetween', 'right', 'value'], '');
          }

          return newFilter;
        }, state.list
      ));
    }

    case RESET_FILTER_OPTIONS: {
      return icepick.setIn(state, ['list'], icepick.map(
        (filter) => {
          if (filter.name !== action.payload) {
            return filter;
          }
          return icepick.setIn(filter, ['options'], filter.options.map(
            (filterOption) => ({ ...filterOption, active: false })
          ));
        }, state.list
      ));
    }
    default: {
      return state;
    }
  }
}
